import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Ticket, Bot, Shield, Zap, Bell, Command, Activity, Layers, Star } from "lucide-react";

const FEATURES = [
  { icon: Ticket, title: "Advanced ticketing", body: "Merge, split, escalate, assign. Every ticket carries a rich activity timeline." },
  { icon: Bot, title: "AI copilot", body: "Claude Sonnet 4.5 drafts replies, summarizes threads, classifies intent, detects spam." },
  { icon: Bell, title: "Real-time inbox", body: "Live notifications, sound cues, browser push. Never miss a critical breach." },
  { icon: Activity, title: "SLA-grade analytics", body: "Response times, CSAT, agent leaderboards, department heatmaps." },
  { icon: Shield, title: "Enterprise security", body: "JWT + Google OAuth, RBAC, brute-force lockouts, audit-ready by design." },
  { icon: Command, title: "Command palette", body: "⌘K anything. Jump between tickets, staff and settings in a heartbeat." },
];

export default function Landing() {
  return (
    <div className="relative min-h-screen bg-background text-foreground overflow-hidden">
      <div className="grain-overlay" aria-hidden />
      <div className="aurora" aria-hidden />

      {/* Nav */}
      <header className="relative z-20 flex items-center justify-between px-6 md:px-12 py-6">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-display font-black glow-primary">L</div>
          <div className="leading-tight">
            <div className="font-display font-black tracking-tighter text-lg">LostDrkC</div>
            <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">Support Core</div>
          </div>
        </div>
        <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
          <a href="#features" className="hover:text-foreground transition-colors">Features</a>
          <a href="#pillars" className="hover:text-foreground transition-colors">Platform</a>
          <a href="#stack" className="hover:text-foreground transition-colors">Stack</a>
        </nav>
        <div className="flex items-center gap-2">
          <Button data-testid="landing-login-btn" asChild variant="ghost" size="sm">
            <Link to="/login">Sign in</Link>
          </Button>
          <Button data-testid="landing-cta-primary" asChild size="sm">
            <Link to="/register">Get started <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 px-6 md:px-12 pt-8 md:pt-16 pb-24 max-w-[1400px] mx-auto">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-card/40 backdrop-blur px-3 py-1 mb-6 font-mono text-[10px] tracking-widest uppercase text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            v1.0 — AI-powered support core
          </div>
          <h1 className="font-display font-black tracking-tighter text-5xl md:text-6xl lg:text-7xl leading-[0.95] mb-6">
            Support that moves at
            <br />
            <span className="text-primary">the speed of your infra.</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed mb-8">
            LostDrkC Support Core is a next-generation, AI-native help desk built for gaming, hosting and Minecraft operators. Ticketing, dashboards, live notifications and Claude Sonnet 4.5 — all wired into one obsidian-black cockpit.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <Button data-testid="hero-cta-primary" asChild size="lg" className="gap-2">
              <Link to="/register">Launch your desk <ArrowRight className="h-4 w-4" /></Link>
            </Button>
            <Button data-testid="hero-cta-secondary" asChild size="lg" variant="outline">
              <Link to="/login">Sign in to demo</Link>
            </Button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-16">
          {[
            { k: "Ticket SLA", v: "< 5 min", desc: "First response" },
            { k: "Automation", v: "AI-first", desc: "Draft, classify, translate" },
            { k: "Realtime", v: "Live", desc: "Websocket-ready" },
            { k: "Uptime", v: "99.99%", desc: "Enterprise-grade" },
          ].map((s) => (
            <div key={s.k} className="p-5 rounded-lg border border-border/60 bg-card/40 backdrop-blur">
              <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">{s.k}</div>
              <div className="font-display font-black text-3xl mt-2 tracking-tighter">{s.v}</div>
              <div className="text-xs text-muted-foreground mt-1">{s.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative z-10 px-6 md:px-12 py-20 max-w-[1400px] mx-auto">
        <div className="grid md:grid-cols-2 gap-6 items-end mb-12">
          <div>
            <div className="font-mono text-[10px] tracking-widest uppercase text-primary mb-3">// Platform</div>
            <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl">Every layer of a modern support desk.</h2>
          </div>
          <p className="text-muted-foreground md:text-right">Modular by construction. Every capability is a swappable module — enable, disable, extend without breaking the core.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div key={title} className="group relative p-6 rounded-lg border border-border/60 bg-card/40 backdrop-blur transition-colors hover:border-primary/50">
              <div className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center mb-4">
                <Icon className="h-4 w-4" />
              </div>
              <div className="font-display font-bold text-lg mb-1">{title}</div>
              <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Marquee */}
      <section id="stack" className="relative z-10 border-y border-border/60 py-8 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee gap-16 font-display font-black text-3xl md:text-5xl tracking-tighter text-muted-foreground/70">
          {[..."TICKETS · TIMELINE · SLA · CSAT · CLAUDE · WEBHOOKS · RBAC · AUDIT · REALTIME · ".split(" · "), ..."TICKETS · TIMELINE · SLA · CSAT · CLAUDE · WEBHOOKS · RBAC · AUDIT · REALTIME · ".split(" · ")].map((t, i) => (
            <span key={i}>{t}</span>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 px-6 md:px-12 py-24 max-w-[1400px] mx-auto text-center">
        <h2 className="font-display font-black tracking-tighter text-4xl md:text-6xl mb-4">Ship a support cockpit today.</h2>
        <p className="text-muted-foreground max-w-2xl mx-auto mb-8">Start with the demo account or sign up in seconds — the whole platform is yours.</p>
        <Button data-testid="footer-cta" asChild size="lg" className="gap-2">
          <Link to="/register">Create your workspace <ArrowRight className="h-4 w-4" /></Link>
        </Button>
      </section>

      <footer className="relative z-10 border-t border-border/60 px-6 md:px-12 py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
        <div>© {new Date().getFullYear()} LostDrkC. Built for operators.</div>
        <div className="font-mono uppercase tracking-widest">support.lostdrkc.in</div>
      </footer>
    </div>
  );
}
